import React, { useState, useMemo } from 'react';
import useSimulationStore from '../../store/simulationStore';
import { shallow } from 'zustand/shallow';

const ReactionsPanel: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<'active' | 'available' | 'editor'>('active');
  const [isEditing, setIsEditing] = useState<boolean>(false);
  
  const {
    activeChemistryConfig,
    availableReactions,
    availableSpecies,
    speciesFocus,
    reactionFocus,
    focusReaction,
    unfocusReaction,
    addReactionToActiveConfig,
    removeReactionFromActiveConfig,
    focusSpecies,
    clearAllFocus
  } = useSimulationStore(state => ({
    activeChemistryConfig: state.activeChemistryConfig,
    availableReactions: state.availableReactions,
    availableSpecies: state.availableSpecies,
    speciesFocus: state.speciesFocus,
    reactionFocus: state.reactionFocus,
    focusReaction: state.focusReaction,
    unfocusReaction: state.unfocusReaction,
    addReactionToActiveConfig: state.addReactionToActiveConfig,
    removeReactionFromActiveConfig: state.removeReactionFromActiveConfig,
    focusSpecies: state.focusSpecies,
    clearAllFocus: state.clearAllFocus
  }), shallow);

  // Get active reactions with focus states
  const activeReactionsWithStates = useMemo(() => {
    return activeChemistryConfig.reactions.map(reaction => {
      // Check if this reaction is directly focused
      const isReactionFocused = reactionFocus[reaction.id] || false;
      
      // Check if any species in this reaction are focused
      const involvedSpeciesIds = new Set([
        ...Object.keys(reaction.reactantsCoeff),
        ...Object.keys(reaction.productsCoeff)
      ]);
      
      const hasFocusedSpecies = Array.from(involvedSpeciesIds).some(speciesId => 
        speciesFocus[speciesId]?.focusLevel === 'focus'
      );
      
      return {
        ...reaction,
        isReactionFocused,
        hasFocusedSpecies,
        involvedSpeciesIds
      };
    });
  }, [activeChemistryConfig.reactions, speciesFocus, reactionFocus]);

  // Get available reactions that aren't already active
  const availableReactionsToAdd = useMemo(() => {
    const activeReactionDefIds = new Set(
      activeChemistryConfig.reactions.map(r => r.reactionDefId)
    );
    
    return availableReactions.filter(reaction => 
      !activeReactionDefIds.has(reaction.id)
    );
  }, [availableReactions, activeChemistryConfig.reactions]);

  const toggleExpansion = () => {
    setIsExpanded(!isExpanded);
  };

  const handleReactionFocus = (reactionId: string) => {
    // Find the reaction to get its species
    const reaction = activeReactionsWithStates.find(r => r.id === reactionId);
    if (reaction) {
      // First, unfocus any species that were directly focused in this reaction
      reaction.involvedSpeciesIds.forEach(speciesId => {
        const currentSpeciesFocus = speciesFocus[speciesId];
        if (currentSpeciesFocus?.focusSource === 'direct') {
          // This species was directly focused, unfocus it
          // We need to call unfocusSpecies directly
          const { unfocusSpecies } = useSimulationStore.getState();
          unfocusSpecies(speciesId);
        }
      });
    }
    
    // Now focus the reaction (which will focus all its species)
    focusReaction(reactionId);
  };

  const handleReactionUnfocus = (reactionId: string) => {
    unfocusReaction(reactionId);
  };

  const handleAddReaction = (reactionDefId: string) => {
    addReactionToActiveConfig(reactionDefId);
  };

  const handleRemoveReaction = (activeReactionId: string) => {
    removeReactionFromActiveConfig(activeReactionId);
  };

  const handleSpeciesFocus = (speciesId: string) => {
    // Check if this species is currently focused by a reaction
    const currentSpeciesFocus = speciesFocus[speciesId];
    const isFocusedByReaction = currentSpeciesFocus?.focusSource === 'reaction';
    
    if (isFocusedByReaction) {
      // If species is focused by reaction, unfocus the reaction first
      // Find which reaction is focusing this species
      const focusingReaction = activeReactionsWithStates.find(reaction => 
        reaction.isReactionFocused && reaction.involvedSpeciesIds.has(speciesId)
      );
      
      if (focusingReaction) {
        handleReactionUnfocus(focusingReaction.id);
      }
    }
    
    // Now focus the species directly
    focusSpecies(speciesId, 'direct');
  };

  const handleClearAllFocus = () => {
    clearAllFocus();
  };

  const toggleEditing = () => {
    setIsEditing(!isEditing);
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle} onClick={toggleExpansion}>
        <span>Reactions</span>
        <div style={headerActionsStyle}>
          <button 
            style={clearButtonStyle}
            onClick={(e) => {
              e.stopPropagation();
              handleClearAllFocus();
            }}
            title="Clear all focus"
          >
            Clear Focus
          </button>
          <button style={buttonStyle}>{isExpanded ? '-' : '+'}</button>
        </div>
      </div>

      {isExpanded && (
        <div style={contentStyle}>
          {/* Tab Navigation */}
          <div style={tabContainerStyle}>
            <button
              style={{
                ...tabButtonStyle,
                ...(activeTab === 'active' ? activeTabStyle : {})
              }}
              onClick={() => setActiveTab('active')}
            >
              Active ({activeReactionsWithStates.length})
            </button>
            <button
              style={{
                ...tabButtonStyle,
                ...(activeTab === 'available' ? activeTabStyle : {})
              }}
              onClick={() => setActiveTab('available')}
            >
              Available ({availableReactionsToAdd.length})
            </button>
            <button
              style={{
                ...tabButtonStyle,
                ...(activeTab === 'editor' ? activeTabStyle : {})
              }}
              onClick={() => setActiveTab('editor')}
            >
              Editor
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'active' && (
            <div style={tabContentStyle}>
              {activeReactionsWithStates.length === 0 ? (
                <div style={placeholderStyle}>No active reactions</div>
              ) : (
                <div style={reactionListStyle}>
                  {activeReactionsWithStates.map((reaction) => (
                    <div key={reaction.id} style={reactionItemStyle}>
                      <div style={reactionHeaderStyle}>
                        <button
                          style={{
                            ...focusButtonStyle,
                            ...(reaction.isReactionFocused ? activeFocusButtonStyle : {})
                          }}
                          onClick={() => 
                            reaction.isReactionFocused 
                              ? handleReactionUnfocus(reaction.id)
                              : handleReactionFocus(reaction.id)
                          }
                          title={reaction.isReactionFocused ? 'Unfocus reaction' : 'Focus reaction'}
                        >
                          {reaction.isReactionFocused ? '👁️' : '👁️‍🗨️'}
                        </button>
                        
                        <div style={reactionInfoStyle}>
                          <span style={reactionNameStyle}>
                            {reaction.reactionDefId}
                          </span>
                          {reaction.isReactionFocused && (
                            <span style={focusedIndicatorStyle}>FOCUSED</span>
                          )}
                        </div>
                        
                        {isEditing && (
                          <button
                            style={removeButtonStyle}
                            onClick={() => handleRemoveReaction(reaction.id)}
                            title="Remove reaction"
                          >
                            ❌
                          </button>
                        )}
                      </div>
                      
                      <div style={reactionDetailsStyle}>
                        <div style={reactionEquationStyle}>
                          <span style={reactantsStyle}>
                            {Object.entries(reaction.reactantsCoeff)
                              .filter(([_, coeff]) => coeff > 0)
                              .map(([speciesId, coeff]) => {
                                const species = availableSpecies.find(s => s.id === speciesId);
                                return `${coeff}${species?.name || speciesId}`;
                              })
                              .join(' + ')
                            }
                          </span>
                          <span style={arrowStyle}>→</span>
                          <span style={productsStyle}>
                            {Object.entries(reaction.productsCoeff)
                              .filter(([_, coeff]) => coeff > 0)
                              .map(([speciesId, coeff]) => {
                                const species = availableSpecies.find(s => s.id === speciesId);
                                return `${coeff}${species?.name || speciesId}`;
                              })
                              .join(' + ')
                            }
                          </span>
                        </div>
                        
                        <div style={speciesListStyle}>
                          {Array.from(reaction.involvedSpeciesIds).map(speciesId => {
                            const species = availableSpecies.find(s => s.id === speciesId);
                            const isFocused = speciesFocus[speciesId]?.focusLevel === 'focus';
                            return (
                              <button
                                key={speciesId}
                                style={{
                                  ...speciesButtonStyle,
                                  ...(isFocused ? activeSpeciesButtonStyle : {})
                                }}
                                onClick={() => handleSpeciesFocus(speciesId)}
                                title={isFocused ? 'Unfocus species' : 'Focus species'}
                              >
                                {species?.name || speciesId}
                                {isFocused && ' 👁️'}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'available' && (
            <div style={tabContentStyle}>
              {availableReactionsToAdd.length === 0 ? (
                <div style={placeholderStyle}>No available reactions to add</div>
              ) : (
                <div style={reactionListStyle}>
                  {availableReactionsToAdd.map((reaction) => (
                    <div key={reaction.id} style={availableReactionItemStyle}>
                      <div style={reactionHeaderStyle}>
                        <span style={reactionNameStyle}>{reaction.id}</span>
                        <button
                          style={addButtonStyle}
                          onClick={() => handleAddReaction(reaction.id)}
                          title="Add to active reactions"
                        >
                          ➕
                        </button>
                      </div>
                      
                      <div style={reactionDetailsStyle}>
                        <div style={reactionEquationStyle}>
                          <span style={reactantsStyle}>
                            {reaction.reactants.map(r => {
                              const species = availableSpecies.find(s => s.id === r.speciesId);
                              return `${r.coefficient}${species?.name || r.speciesId}`;
                            }).join(' + ')}
                          </span>
                          <span style={arrowStyle}>→</span>
                          <span style={productsStyle}>
                            {reaction.products.map(p => {
                              const species = availableSpecies.find(s => s.id === p.speciesId);
                              return `${p.coefficient}${species?.name || p.speciesId}`;
                            }).join(' + ')}
                          </span>
                        </div>
                        
                        {reaction.description && (
                          <div style={descriptionStyle}>{reaction.description}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'editor' && (
            <div style={tabContentStyle}>
              <div style={editorPlaceholderStyle}>
                <h4>Reaction Editor</h4>
                <p>Drag-and-drop reaction builder coming soon!</p>
                <p>This will allow you to:</p>
                <ul style={featureListStyle}>
                  <li>Drag species to build reactions</li>
                  <li>Set energy thresholds</li>
                  <li>Configure reaction parameters</li>
                  <li>Preview reaction equations</li>
                </ul>
              </div>
            </div>
          )}

          {/* Edit Toggle */}
          <div style={editToggleStyle}>
            <button
              style={{
                ...editToggleButtonStyle,
                ...(isEditing ? activeEditToggleStyle : {})
              }}
              onClick={toggleEditing}
            >
              {isEditing ? '✏️ Editing' : '✏️ Edit'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Styles
const containerStyle: React.CSSProperties = {
  position: 'absolute',
  top: '10px',
  left: '300px', // Position next to SpeciesPanel
  width: 'auto',
  minWidth: '350px',
  maxWidth: '45vw',
  maxHeight: '500px',
  backgroundColor: 'rgba(40, 40, 40, 0.9)',
  color: '#eee',
  border: '1px solid #555',
  borderRadius: '4px',
  fontSize: '12px',
  fontFamily: 'monospace',
  zIndex: 100,
  display: 'flex',
  flexDirection: 'column',
};

const headerStyle: React.CSSProperties = {
  padding: '8px 12px',
  backgroundColor: 'rgba(60, 60, 60, 0.9)',
  fontWeight: 'bold',
  flexShrink: 0,
  borderBottom: '1px solid #555',
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const headerActionsStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const buttonStyle: React.CSSProperties = {
  background: 'none',
  border: '1px solid #888',
  color: '#eee',
  cursor: 'pointer',
  padding: '2px 6px',
  fontSize: '10px',
  lineHeight: '1',
};

const clearButtonStyle: React.CSSProperties = {
  background: 'rgba(200, 60, 60, 0.8)',
  border: '1px solid #aa4444',
  color: '#fff',
  cursor: 'pointer',
  padding: '2px 6px',
  fontSize: '10px',
  lineHeight: '1',
  borderRadius: '2px',
};

const contentStyle: React.CSSProperties = {
  overflowY: 'auto',
  padding: '8px',
  flexGrow: 1,
  minHeight: 0,
};

const tabContainerStyle: React.CSSProperties = {
  display: 'flex',
  gap: '4px',
  marginBottom: '8px',
  borderBottom: '1px solid #444',
  paddingBottom: '4px',
};

const tabButtonStyle: React.CSSProperties = {
  background: 'rgba(60, 60, 60, 0.8)',
  border: '1px solid #666',
  color: '#ccc',
  cursor: 'pointer',
  padding: '4px 8px',
  fontSize: '11px',
  borderRadius: '2px',
};

const activeTabStyle: React.CSSProperties = {
  background: 'rgba(0, 150, 255, 0.8)',
  border: '1px solid #0088ff',
  color: '#fff',
};

const tabContentStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
};

const placeholderStyle: React.CSSProperties = {
  fontStyle: 'italic',
  color: '#aaa',
  textAlign: 'center',
  padding: '20px',
};

const reactionListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const reactionItemStyle: React.CSSProperties = {
  backgroundColor: 'rgba(30, 30, 30, 0.8)',
  border: '1px solid #444',
  borderRadius: '4px',
  padding: '8px',
};

const availableReactionItemStyle: React.CSSProperties = {
  backgroundColor: 'rgba(30, 30, 30, 0.8)',
  border: '1px solid #444',
  borderRadius: '4px',
  padding: '8px',
  opacity: 0.9,
};

const reactionHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '6px',
};

const focusButtonStyle: React.CSSProperties = {
  background: 'rgba(60, 60, 60, 0.8)',
  border: '1px solid #666',
  color: '#ccc',
  cursor: 'pointer',
  padding: '2px 6px',
  fontSize: '10px',
  borderRadius: '2px',
};

const activeFocusButtonStyle: React.CSSProperties = {
  background: 'rgba(0, 150, 255, 0.8)',
  border: '1px solid #0088ff',
  color: '#fff',
};

const reactionInfoStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  flex: 1,
};

const reactionNameStyle: React.CSSProperties = {
  fontWeight: 'bold',
  fontSize: '13px',
};

const focusedIndicatorStyle: React.CSSProperties = {
  backgroundColor: 'rgba(0, 150, 255, 0.8)',
  color: '#fff',
  fontSize: '8px',
  fontWeight: 'bold',
  padding: '1px 4px',
  borderRadius: '2px',
};

const removeButtonStyle: React.CSSProperties = {
  background: 'rgba(200, 60, 60, 0.8)',
  border: '1px solid #aa4444',
  color: '#fff',
  cursor: 'pointer',
  padding: '2px 4px',
  fontSize: '10px',
  borderRadius: '2px',
};

const addButtonStyle: React.CSSProperties = {
  background: 'rgba(60, 150, 60, 0.8)',
  border: '1px solid #44aa44',
  color: '#fff',
  cursor: 'pointer',
  padding: '2px 6px',
  fontSize: '10px',
  borderRadius: '2px',
};

const reactionDetailsStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const reactionEquationStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '11px',
  fontFamily: 'monospace',
};

const reactantsStyle: React.CSSProperties = {
  color: '#ffb74d',
};

const arrowStyle: React.CSSProperties = {
  color: '#81c784',
  fontWeight: 'bold',
};

const productsStyle: React.CSSProperties = {
  color: '#4fc3f7',
};

const speciesListStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '4px',
};

const speciesButtonStyle: React.CSSProperties = {
  background: 'rgba(60, 60, 60, 0.8)',
  border: '1px solid #666',
  color: '#ccc',
  cursor: 'pointer',
  padding: '2px 6px',
  fontSize: '9px',
  borderRadius: '2px',
};

const activeSpeciesButtonStyle: React.CSSProperties = {
  background: 'rgba(0, 150, 255, 0.8)',
  border: '1px solid #0088ff',
  color: '#fff',
};

const descriptionStyle: React.CSSProperties = {
  fontSize: '10px',
  color: '#aaa',
  fontStyle: 'italic',
};

const editorPlaceholderStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '20px',
  color: '#aaa',
};

const featureListStyle: React.CSSProperties = {
  textAlign: 'left',
  fontSize: '10px',
  color: '#ccc',
};

const editToggleStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  marginTop: '8px',
  paddingTop: '8px',
  borderTop: '1px solid #444',
};

const editToggleButtonStyle: React.CSSProperties = {
  background: 'rgba(60, 60, 60, 0.8)',
  border: '1px solid #666',
  color: '#ccc',
  cursor: 'pointer',
  padding: '4px 8px',
  fontSize: '11px',
  borderRadius: '2px',
};

const activeEditToggleStyle: React.CSSProperties = {
  background: 'rgba(255, 150, 0, 0.8)',
  border: '1px solid #ff8800',
  color: '#fff',
};

export default ReactionsPanel;
